// Import api model.
import {
	PerilDeductible,
	PossibleDeductible,
	PossiblePeril,
} from '@app/models/api';
// Import enumerations.
import {
	Peril,
} from '@app/enumerations';

export default class PerilService {

	private static instance: PerilService;

	private constructor() {

	}

	public static getInstance(): PerilService {
		if (PerilService.instance === undefined) {
			PerilService.instance = new PerilService();
		}
		return PerilService.instance;
	}

	public copyPerilDeductibles = (perilDeductibles: PerilDeductible[]): PerilDeductible[] => {
		return perilDeductibles.map((perilDeductibles: PerilDeductible) => {
			let copiedPerilDeductible: PerilDeductible = new PerilDeductible();
			copiedPerilDeductible.perilCode = perilDeductibles.perilCode;
			copiedPerilDeductible.deductibleCode = perilDeductibles.deductibleCode;
			return copiedPerilDeductible;
		});
	}

	public correctPerilDeductibles = (perilDeductibles: PerilDeductible[], possiblePerils: PossiblePeril[]): PerilDeductible[] => {
		let validPerilDeductibles: PerilDeductible[] = perilDeductibles.filter((perilDeductible: PerilDeductible) => {
			return perilDeductible.perilCode !== null && possiblePerils.find((possiblePeril: PossiblePeril) => possiblePeril.isEligible && possiblePeril.code === perilDeductible.perilCode) !== undefined;
		});

		return validPerilDeductibles.map((validPerilDeductible: PerilDeductible) => {
			// Search for the matching deductible code.
			let matchingDeductibleCode: string | undefined = this.findMatchingDeductibleCode(validPerilDeductible, possiblePerils);
			// If there are no matches, then by default set the deductible code to null.
			if (matchingDeductibleCode === undefined) {
				validPerilDeductible.deductibleCode = null;
			}
			return validPerilDeductible;
		});
	}

	public findMatchingDeductibleCode = (perilDeductible: PerilDeductible, possiblePerils: PossiblePeril[]): string | undefined => {
		let matchingDeductibleCode: string | undefined = undefined;
		// Continue searching the matching deductible code if neither the peril and deductible code are null.
		if (perilDeductible.perilCode !== null && perilDeductible.deductibleCode !== null) {
			// Find the matching possible peril based on the peril code.
			let matchingPossiblePeril: PossiblePeril | undefined = this.findMatchingPossiblePeril(perilDeductible, possiblePerils);
			// If there is no matching possible peril, then do not attempt to search for the deductible code.
			if (matchingPossiblePeril !== undefined) {
				// Find the matching deductible code based on the deductible code.
				matchingDeductibleCode = this.getPossibleDeductibleCodes(matchingPossiblePeril).find((possibleDeductibleCode: string) => possibleDeductibleCode === perilDeductible.deductibleCode);
			}
		}

		return matchingDeductibleCode;
	}

	public findMatchingPossiblePeril = (perilDeductible: PerilDeductible, possiblePerils: PossiblePeril[]): PossiblePeril | undefined => {
		let matchingPossiblePeril: PossiblePeril | undefined = undefined;
		// Continue searching the matching peril code if the peril code is not null.
		if (perilDeductible.perilCode !== null) {
			// Find the matching possible peril based on the peril code.
			matchingPossiblePeril = possiblePerils.find((possiblePeril: PossiblePeril) => possiblePeril.code === perilDeductible.perilCode && possiblePeril.isEligible);
		}

		return matchingPossiblePeril;
	}

	public getIneligibleSelectedPerils = (perilDeductibles: PerilDeductible[], possiblePerils: PossiblePeril[]): PossiblePeril[] => {
		return perilDeductibles
			.map((perilDeductible) => possiblePerils.find((possiblePeril) => !possiblePeril.isEligible && possiblePeril.code === perilDeductible.perilCode))
			.filter((possiblePeril): possiblePeril is PossiblePeril => possiblePeril !== undefined);
	}

	public areAllNonDeprecatedPossiblePerilsIneligible = (possiblePerils: PossiblePeril[]): boolean => {
		return possiblePerils
			.filter((possiblePeril) => !possiblePeril.isDeprecated)
			.every((possiblePeril) => !possiblePeril.isEligible)
	}

	public getNonDeprecatedIneligiblePerils = (possiblePerils: PossiblePeril[]): PossiblePeril[] => {
		return possiblePerils
			.filter((possiblePeril) => !possiblePeril.isDeprecated && !possiblePeril.isEligible);
	}

	public getIneligibilityReasonsMessageForPossibleDeductible = (possibleDeductible: PossibleDeductible): string => {
		let message: string = possibleDeductible.reasonsForIneligibility.join(' ');
		if (String.isNullOrWhiteSpace(message)) {
			message = 'This deductible is ineligible for coverage.';
		}
		return message;
	}

	public getEligiblePossibleDeductibles = (possiblePeril: PossiblePeril | undefined): PossibleDeductible[] => {
		return possiblePeril?.possibleDeductibles.filter((possibleDeductible) => possibleDeductible.isEligible) ?? [];
	}

	public getPossibleDeductibleCodes = (possiblePeril: PossiblePeril | undefined, includeUnallowedCodes: boolean = false): string[] => {
		return possiblePeril?.possibleDeductibles
			.filter((possibleDeductible) => includeUnallowedCodes || possibleDeductible.isEligible)
			.map((possibleDeductible) => possibleDeductible.code ?? String.empty())
			.filter((possibleDeductibleCode) => !String.isNullOrWhiteSpace(possibleDeductibleCode))
			?? [];
	}
};